import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getApiUrl } from '../config/api';
import './Envase.css';

const hoy = () => new Date().toISOString().split('T')[0];

const fmt = (v, dec = 2) => {
    if (v == null || v === '') return '0,00';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const fecha = (f) => {
    if (!f) return '';
    const [y, m, d] = String(f).split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

const chipEstado = (e) => {
    const map = {
        P: { txt: 'Pendiente', bg: '#E4F2FC', color: '#004F9F', ico: '🟢' },
        A: { txt: 'Anulada',   bg: '#FFEBEE', color: '#C62828', ico: '🚫' }
    };
    const s = map[e] || map.P;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
            backgroundColor: s.bg, color: s.color, fontSize: '11px', fontWeight: '600'
        }}>{s.ico} {s.txt}</span>
    );
};

const formInicial = () => ({
    ordenpago: '',
    fecha: hoy(),
    idSucursal: 1,
    idProveedor: '',
    nomProveedor: '',
    variosProveedores: false,
    idMoneda: 'GS',
    tipocambio: 1,
    idConcepto: '',
    idClaspago: '',
    detalle: '',
    retencion: 0,
    idOperador: ''
});

function BOPago({ usuario }) {
    const [ordenes, setOrdenes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(10);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [ordenVer, setOrdenVer] = useState(null);

    // Modal alta
    const [formulario, setFormulario] = useState(formInicial());
    const [busqProv, setBusqProv] = useState('');
    const [provsEncontrados, setProvsEncontrados] = useState([]);
    const [facPend, setFacPend] = useState([]);      // facturas pendientes del proveedor
    const [facSel, setFacSel] = useState({});         // { "clave": importe }
    const [medios, setMedios] = useState([]);         // filas de medios de pago

    // Catalogos
    const [tiposDoc, setTiposDoc] = useState([]);
    const [bancos, setBancos] = useState([]);
    const [sucursales, setSucursales] = useState([]);

    // "Varios Proveedores": buscador auxiliar para agregar facturas
    const [busqProvVarios, setBusqProvVarios] = useState('');
    const [provsEncontradosVarios, setProvsEncontradosVarios] = useState([]);
    const [provsUsados, setProvsUsados] = useState([]);   // { id_proveedor, nom_proveedor }[]

    useEffect(() => {
        cargar();
        cargarCatalogos();
    }, []);

    const cargar = async () => {
        try {
            const { data } = await axios.get('/api/bopago');
            setOrdenes(data);
        } catch (e) { console.error(e); }
    };
    const cargarCatalogos = async () => {
        try {
            const [t, b, s] = await Promise.all([
                axios.get('/api/bopago/tipos-doc'),
                axios.get('/api/bopago/bancos'),
                axios.get('/api/bopago/sucursales')
            ]);
            setTiposDoc(t.data);
            setBancos(b.data);
            setSucursales(s.data);
        } catch (e) { console.error(e); }
    };

    // ---------- Filtros + paginacion ----------
    const filtradas = ordenes.filter(o => {
        const b = busqueda.toLowerCase();
        const okBus = !b || String(o.ordenpago).includes(b)
            || String(o.nom_proveedor || '').toLowerCase().includes(b);
        const okD = !fechaDesde || (o.fecha && String(o.fecha) >= fechaDesde);
        const okH = !fechaHasta || (o.fecha && String(o.fecha) <= fechaHasta);
        return okBus && okD && okH;
    });
    const totalPags = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const pActual = Math.min(pagina, totalPags);
    const paginadas = filtradas.slice((pActual - 1) * porPagina, pActual * porPagina);

    const exportar = () => {
        const filas = filtradas.map(o => ({
            'N° Orden': o.ordenpago,
            'Fecha': fecha(o.fecha),
            'Proveedor': o.nom_proveedor,
            'Monto': o.monto,
            'Retencion': o.retencion,
            'Moneda': o.id_moneda,
            'Banco': o.nom_banco,
            'Detalle': o.detalle,
            'Estado': o.estado
        }));
        const ws = XLSX.utils.json_to_sheet(filas);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'OrdenesPago');
        XLSX.writeFile(wb, `ordenes_pago_${hoy()}.xlsx`);
    };

    // ---------- Alta ----------
    const abrirModal = () => {
        setFormulario({ ...formInicial(), idOperador: usuario || '' });
        setBusqProv(''); setProvsEncontrados([]);
        setBusqProvVarios(''); setProvsEncontradosVarios([]);
        setProvsUsados([]);
        setFacPend([]); setFacSel({}); setMedios([]);
        setModalAbierto(true);
    };
    const cerrarModal = () => setModalAbierto(false);

    // Buscar proveedor
    useEffect(() => {
        if (busqProv.length < 2) { setProvsEncontrados([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await axios.get('/api/bopago/buscar-proveedor',
                    { params: { busqueda: busqProv } });
                setProvsEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
        return () => clearTimeout(t);
    }, [busqProv]);

    const seleccionarProveedor = async (p) => {
        setFormulario(f => ({ ...f, idProveedor: p.id_proveedor, nomProveedor: p.nom_proveedor }));
        setBusqProv(''); setProvsEncontrados([]);
        // Cargar facturas pendientes
        try {
            const { data } = await axios.get('/api/bopago/facturas-pendientes',
                { params: { idProveedor: p.id_proveedor } });
            setFacPend(data);
            setFacSel({});
        } catch (e) { console.error(e); setFacPend([]); }
    };

    // Buscar proveedor (modo Varios): mismo endpoint pero acumula facturas
    useEffect(() => {
        if (busqProvVarios.length < 2) { setProvsEncontradosVarios([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await axios.get('/api/bopago/buscar-proveedor',
                    { params: { busqueda: busqProvVarios } });
                setProvsEncontradosVarios(r.data);
            } catch (e) { console.error(e); }
        }, 250);
        return () => clearTimeout(t);
    }, [busqProvVarios]);

    // Agregar proveedor a la lista de VARIOS y traer sus facturas pendientes
    const agregarProveedorVarios = async (p) => {
        if (provsUsados.find(x => x.id_proveedor === p.id_proveedor)) {
            alert('Ese proveedor ya esta en la lista');
            return;
        }
        setProvsUsados(prev => [...prev, p]);
        setBusqProvVarios(''); setProvsEncontradosVarios([]);
        // Cargar facturas de TODOS los proveedores actuales
        const ids = [...provsUsados.map(x => x.id_proveedor), p.id_proveedor];
        try {
            const { data } = await axios.post('/api/bopago/facturas-pendientes-multi', ids);
            setFacPend(data);
        } catch (e) { console.error(e); }
    };
    const quitarProveedorVarios = async (idProv) => {
        const nuevos = provsUsados.filter(x => x.id_proveedor !== idProv);
        setProvsUsados(nuevos);
        // Quitar tildes de facturas de ese proveedor
        setFacSel(prev => {
            const nuevo = { ...prev };
            Object.keys(nuevo).forEach(k => {
                const idP = k.split('|')[5];
                if (Number(idP) === idProv) delete nuevo[k];
            });
            return nuevo;
        });
        if (nuevos.length === 0) { setFacPend([]); return; }
        try {
            const { data } = await axios.post('/api/bopago/facturas-pendientes-multi',
                nuevos.map(x => x.id_proveedor));
            setFacPend(data);
        } catch (e) { console.error(e); }
    };

    // Toggle Varios Proveedores
    const toggleVarios = (v) => {
        setFormulario(f => ({ ...f, variosProveedores: v, idProveedor: v ? 0 : '', nomProveedor: '' }));
        setBusqProv(''); setProvsEncontrados([]);
        setBusqProvVarios(''); setProvsEncontradosVarios([]);
        setProvsUsados([]);
        setFacPend([]); setFacSel({});
    };

    // Clave de factura: incluye id_proveedor para soportar varios
    const claveFactura = (f) => `${f.tipo_doc}|${f.nfactura}|${f.timbrado}|${f.sucursal}|${f.boca}|${f.id_proveedor}`;

    const toggleFactura = (f) => {
        const k = claveFactura(f);
        setFacSel(prev => {
            const nuevo = { ...prev };
            if (k in nuevo) delete nuevo[k];
            else nuevo[k] = Number(f.saldo) || 0;   // default = pagar el saldo completo
            return nuevo;
        });
    };
    const cambiarImporteFactura = (f, v) => {
        const k = claveFactura(f);
        setFacSel(prev => ({ ...prev, [k]: Number(v) || 0 }));
    };

    const totalImputado = Object.values(facSel).reduce((s, v) => s + Number(v || 0), 0);
    const totalMedios   = medios.reduce((s, m) => s + Number(m.monto || 0), 0);

    // Medios de pago. El campo "detalle" representa "A Nombre de" (para cheques)
    const agregarMedio = () => {
        setMedios(prev => [...prev, {
            tipoDoc: 'EFE',   // default efectivo
            idBanco: '',
            nrocheque: '',
            fecha: formulario.fecha,
            vencimiento: '',
            monto: '',
            detalle: ''       // "A Nombre de"
        }]);
    };
    const actualizarMedio = (idx, campo, valor) => {
        setMedios(prev => prev.map((m, i) => i === idx ? { ...m, [campo]: valor } : m));
    };
    const quitarMedio = (idx) => {
        setMedios(prev => prev.filter((_, i) => i !== idx));
    };
    const esCheque = (m) => (m.tipoDoc || '').toUpperCase() === 'CHE';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formulario.variosProveedores) {
            if (provsUsados.length === 0) return alert('Agregue al menos un proveedor');
        } else {
            if (!formulario.idProveedor) return alert('Seleccione un proveedor');
        }
        if (Object.keys(facSel).length === 0) return alert('Tilde al menos una factura');
        if (medios.length === 0) return alert('Agregue al menos un medio de pago');

        // Validar diferencia
        const retencion = Number(formulario.retencion) || 0;
        const diff = Math.abs(totalImputado - totalMedios - retencion);
        if (diff > 0.01) {
            return alert(`Diferencia: imputado ${fmt(totalImputado)} != medios ${fmt(totalMedios)} + retencion ${fmt(retencion)}`);
        }

        // Armar payload de facturas (clave con | y 6 partes)
        const facturas = Object.entries(facSel).map(([k, importe]) => {
            const [tipoDoc, nfacturaStr, timbrado, sucursalStr, boca, idProvStr] = k.split('|');
            return {
                tipoDoc,
                nfactura: Number(nfacturaStr),
                timbrado,
                sucursal: Number(sucursalStr),
                boca,
                idProveedor: Number(idProvStr),
                importe: Number(importe)
            };
        });

        const payload = {
            ...formulario,
            // Si es "Varios Proveedores" -> id_proveedor=0 (cada fila trae el suyo)
            idProveedor: formulario.variosProveedores ? 0 : Number(formulario.idProveedor),
            tipocambio: Number(formulario.tipocambio) || 1,
            retencion,
            medios,
            facturas
        };

        try {
            const { data } = await axios.post('/api/bopago', payload);
            alert(`Orden de pago #${data.ordenpago} creada.\n` +
                  `Facturas: ${data.facturas}, Medios: ${data.medios}, Total: ${fmt(data.monto)}`);
            cerrarModal();
            cargar();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar');
        }
    };

    // ---------- Ver / Anular ----------
    const abrirVer = async (o) => {
        try {
            const { data } = await axios.get(`/api/bopago/${o.ordenpago}`);
            setOrdenVer(data);
            setModalVerAbierto(true);
        } catch (e) { alert('Error al cargar detalle'); }
    };

    // Imprimir: abre el PDF (con logo de empresa) en una pestana nueva
    const imprimir = (o) => {
        window.open(getApiUrl(`/api/reportes/orden-pago/${o.ordenpago}`), '_blank');
    };

    const anular = async (o) => {
        if (!window.confirm(`Anular orden de pago #${o.ordenpago}? ` +
            `Se restaurara el saldo de todas las facturas imputadas.`)) return;
        try {
            await axios.put(`/api/bopago/${o.ordenpago}/anular`);
            alert('Orden anulada. Saldos restaurados.');
            cargar();
        } catch (e) {
            alert(e.response?.data?.message || 'Error al anular');
        }
    };

    // ---------- Render ----------
    return (
        <div style={{ padding: '20px', fontFamily: 'Gilam, sans-serif' }}>
            <h2 style={{ color: '#004F9F', marginBottom: '15px' }}>Ordenes de Pago a Proveedores</h2>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="text" placeholder="Buscar (nro / proveedor)"
                    value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                    style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '240px' }} />
                <label>Desde: <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} /></label>
                <label>Hasta: <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }} /></label>
                <button onClick={abrirModal} style={{
                    padding: '8px 16px', background: '#004F9F', color: '#fff',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
                }}>+ Nueva Orden de Pago</button>
                <button onClick={exportar} style={{
                    padding: '8px 16px', background: '#4CAF50', color: '#fff',
                    border: 'none', borderRadius: '4px', cursor: 'pointer'
                }}>📊 Excel</button>
            </div>

            {/* Grilla */}
            <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#004F9F', color: '#fff' }}>
                        <tr>
                            <th style={{ padding: '8px', textAlign: 'left' }}>N°</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Fecha</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Proveedor</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Monto</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Retencion</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Moneda</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Cta. Banco</th>
                            <th style={{ padding: '8px', textAlign: 'center' }}>Estado</th>
                            <th style={{ padding: '8px', textAlign: 'center' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginadas.map(o => (
                            <tr key={o.ordenpago} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '6px' }}><strong>#{o.ordenpago}</strong></td>
                                <td style={{ padding: '6px' }}>{fecha(o.fecha)}</td>
                                <td style={{ padding: '6px' }}>{o.nom_proveedor}</td>
                                <td style={{ padding: '6px', textAlign: 'right' }}>{fmt(o.monto)}</td>
                                <td style={{ padding: '6px', textAlign: 'right' }}>{fmt(o.retencion)}</td>
                                <td style={{ padding: '6px' }}>{o.id_moneda}</td>
                                <td style={{ padding: '6px', fontSize: '12px' }}>{o.nom_banco || '-'}</td>
                                <td style={{ padding: '6px', textAlign: 'center' }}>{chipEstado(o.estado)}</td>
                                <td style={{ padding: '6px', textAlign: 'center' }}>
                                    <button onClick={() => abrirVer(o)} title="Ver"
                                        style={{ marginRight: '4px', background: 'none', border: '1px solid #ccc',
                                                 borderRadius: '4px', padding: '3px 8px', cursor: 'pointer' }}>👁</button>
                                    <button onClick={() => imprimir(o)} title="Imprimir PDF"
                                        style={{ marginRight: '4px', background: 'none', border: '1px solid #004F9F',
                                                 color: '#004F9F', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer' }}>🖨</button>
                                    {o.estado === 'P' && (
                                        <button onClick={() => anular(o)} title="Anular"
                                            style={{ background: 'none', border: '1px solid #C62828',
                                                     color: '#C62828', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer' }}>🚫</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {paginadas.length === 0 && (
                            <tr><td colSpan="9" style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                                Sin ordenes de pago
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button disabled={pActual === 1} onClick={() => setPagina(p => p - 1)}>‹</button>
                <span>Pag {pActual} de {totalPags}</span>
                <button disabled={pActual === totalPags} onClick={() => setPagina(p => p + 1)}>›</button>
                <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))}>
                    {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / pag</option>)}
                </select>
                <span style={{ color: '#666' }}>{filtradas.length} orden(es)</span>
            </div>

            {/* ================= MODAL NUEVA ORDEN DE PAGO ================= */}
            {modalAbierto && (
                <div className="atm-modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'flex-start', padding: '15px', zIndex: 1000,
                    overflowY: 'auto'
                }}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px',
                                 width: '96%', maxWidth: '1200px' }}>
                        <h3 style={{ color: '#004F9F', marginTop: 0 }}>Nueva Orden de Pago</h3>
                        <form onSubmit={handleSubmit}>
                            {/* Cabecera */}
                            <div style={{ padding: '10px', background: '#F5F9FE', borderRadius: '4px', marginBottom: '10px' }}>
                                {/* Fila 1: Nro | Fecha | Sucursal */}
                                <div style={{ display: 'grid', gridTemplateColumns: '150px 150px 1fr', gap: '10px' }}>
                                    <div>
                                        <label style={lblSt}>Nro Orden Pago</label>
                                        <input type="text" value="(automatico)" disabled
                                            style={{ ...inputSt, background: '#EEE', color: '#888' }} />
                                    </div>
                                    <div>
                                        <label style={lblSt}>Fecha *</label>
                                        <input type="date" value={formulario.fecha} required
                                            onChange={(e) => setFormulario(f => ({ ...f, fecha: e.target.value }))}
                                            style={inputSt} />
                                    </div>
                                    <div>
                                        <label style={lblSt}>Sucursal *</label>
                                        <select value={formulario.idSucursal}
                                            onChange={(e) => setFormulario(f => ({ ...f, idSucursal: Number(e.target.value) }))}
                                            style={inputSt}>
                                            {sucursales.map(s => (
                                                <option key={s.id_sucursal} value={s.id_sucursal}>
                                                    {s.id_sucursal} - {s.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Fila 2: Proveedor / Varios Proveedores */}
                                <div style={{ marginTop: '10px', display: 'flex', gap: '15px', alignItems: 'end' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <label style={lblSt}>
                                            {formulario.variosProveedores ? 'Proveedores (varios)' : 'Cod Proveedor *'}
                                        </label>
                                        {formulario.variosProveedores ? (
                                            /* Modo VARIOS: chips de proveedores + buscador para agregar */
                                            <div>
                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap',
                                                             marginBottom: '4px', minHeight: '28px' }}>
                                                    {provsUsados.map(p => (
                                                        <span key={p.id_proveedor} style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            padding: '3px 8px', background: '#E4F2FC',
                                                            borderRadius: '12px', fontSize: '12px'
                                                        }}>
                                                            <strong>{p.id_proveedor}</strong> · {p.nom_proveedor}
                                                            <button type="button"
                                                                onClick={() => quitarProveedorVarios(p.id_proveedor)}
                                                                style={{ background: 'none', border: 'none',
                                                                         color: '#C62828', cursor: 'pointer',
                                                                         padding: 0, fontSize: '14px' }}>×</button>
                                                        </span>
                                                    ))}
                                                    {provsUsados.length === 0 && (
                                                        <span style={{ color: '#888', fontSize: '12px', padding: '4px' }}>
                                                            Agregue proveedores con el buscador de abajo
                                                        </span>
                                                    )}
                                                </div>
                                                <input type="text" placeholder="Buscar y agregar proveedor..."
                                                    value={busqProvVarios}
                                                    onChange={(e) => setBusqProvVarios(e.target.value)}
                                                    style={inputSt} />
                                                {provsEncontradosVarios.length > 0 && (
                                                    <div style={{
                                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                                        background: '#fff', border: '1px solid #004F9F',
                                                        borderRadius: '4px', maxHeight: '180px', overflowY: 'auto',
                                                        zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                                    }}>
                                                        {provsEncontradosVarios.map(p => (
                                                            <div key={p.id_proveedor} onClick={() => agregarProveedorVarios(p)}
                                                                style={{ padding: '6px 10px', cursor: 'pointer',
                                                                         borderBottom: '1px solid #eee' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                                <strong>{p.nom_proveedor}</strong>
                                                                <span style={{ color: '#666', marginLeft: '8px' }}>RUC {p.ruc_prov}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : formulario.idProveedor ? (
                                            <div style={{ display: 'flex', gap: '6px', padding: '6px 8px',
                                                         background: '#E4F2FC', borderRadius: '4px', alignItems: 'center' }}>
                                                <strong>{formulario.idProveedor}</strong> - {formulario.nomProveedor}
                                                <button type="button" style={{
                                                    marginLeft: 'auto', background: 'none', border: 'none',
                                                    color: '#C62828', cursor: 'pointer'
                                                }} onClick={() => {
                                                    setFormulario(f => ({ ...f, idProveedor: '', nomProveedor: '' }));
                                                    setFacPend([]); setFacSel({});
                                                }}>✕ Cambiar</button>
                                            </div>
                                        ) : (
                                            <input type="text" placeholder="Buscar por nombre / RUC / codigo"
                                                value={busqProv} onChange={(e) => setBusqProv(e.target.value)}
                                                style={inputSt} />
                                        )}
                                        {!formulario.variosProveedores && provsEncontrados.length > 0 && (
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, right: 0,
                                                background: '#fff', border: '1px solid #004F9F',
                                                borderRadius: '4px', maxHeight: '180px', overflowY: 'auto',
                                                zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}>
                                                {provsEncontrados.map(p => (
                                                    <div key={p.id_proveedor} onClick={() => seleccionarProveedor(p)}
                                                        style={{ padding: '6px 10px', cursor: 'pointer',
                                                                 borderBottom: '1px solid #eee' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                        <strong>{p.nom_proveedor}</strong>
                                                        <span style={{ color: '#666', marginLeft: '8px' }}>RUC {p.ruc_prov}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px',
                                                 whiteSpace: 'nowrap', paddingBottom: '6px' }}>
                                        <input type="checkbox" id="varios"
                                            checked={formulario.variosProveedores}
                                            onChange={(e) => toggleVarios(e.target.checked)} />
                                        <label htmlFor="varios" style={{ fontSize: '13px', cursor: 'pointer' }}>
                                            Varios Proveedores
                                        </label>
                                    </div>
                                </div>

                                {/* Fila 3: Moneda | Cambio */}
                                <div style={{ marginTop: '10px', display: 'grid',
                                             gridTemplateColumns: '150px 150px 1fr', gap: '10px' }}>
                                    <div>
                                        <label style={lblSt}>Moneda</label>
                                        <select value={formulario.idMoneda}
                                            onChange={(e) => setFormulario(f => ({ ...f, idMoneda: e.target.value }))}
                                            style={inputSt}>
                                            <option value="GS">GS - Guaranies</option>
                                            <option value="USD">USD - Dolares</option>
                                            <option value="EUR">EUR - Euros</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={lblSt}>Cambio</label>
                                        <input type="number" min="0.01" step="0.01"
                                            value={formulario.tipocambio}
                                            onChange={(e) => setFormulario(f => ({ ...f, tipocambio: e.target.value }))}
                                            style={{ ...inputSt, textAlign: 'right' }} />
                                    </div>
                                    <div></div>
                                </div>

                                {/* Fila 4: Concepto */}
                                <div style={{ marginTop: '10px' }}>
                                    <label style={lblSt}>Concepto</label>
                                    <input type="text" value={formulario.detalle} maxLength={160}
                                        onChange={(e) => setFormulario(f => ({ ...f, detalle: e.target.value }))}
                                        style={inputSt} />
                                </div>
                            </div>

                            {/* Facturas pendientes */}
                            {(formulario.idProveedor || provsUsados.length > 0) && (
                                <div style={{ padding: '10px', background: '#FFF8E1', borderRadius: '4px', marginBottom: '10px' }}>
                                    <h4 style={{ margin: '0 0 6px 0', color: '#E65100' }}>
                                        Facturas a Pagar ({facPend.length})
                                        <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                                            Tilde y edite el importe a imputar (default = saldo)
                                        </span>
                                    </h4>
                                    {facPend.length === 0 ? (
                                        <p style={{ margin: 0, color: '#888' }}>Sin facturas pendientes de pago</p>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                <thead style={{ background: '#FFB300', color: '#fff' }}>
                                                    <tr>
                                                        <th style={thSt}></th>
                                                        {formulario.variosProveedores && <th style={thSt}>Proveedor</th>}
                                                        <th style={thSt}>Tipo</th>
                                                        <th style={thSt}>N° Factura</th>
                                                        <th style={thSt}>Fecha</th>
                                                        <th style={thSt}>Timbrado</th>
                                                        <th style={{...thSt, textAlign: 'right'}}>Total</th>
                                                        <th style={{...thSt, textAlign: 'right'}}>Saldo</th>
                                                        <th style={{...thSt, textAlign: 'right'}}>Importe *</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {facPend.map(f => {
                                                        const k = claveFactura(f);
                                                        const tildado = k in facSel;
                                                        return (
                                                            <tr key={k} style={{
                                                                borderBottom: '1px solid #eee',
                                                                background: tildado ? '#FFF8E1' : 'transparent'
                                                            }}>
                                                                <td style={{ padding: '4px', textAlign: 'center' }}>
                                                                    <input type="checkbox" checked={tildado}
                                                                        onChange={() => toggleFactura(f)} />
                                                                </td>
                                                                {formulario.variosProveedores && (
                                                                    <td style={{ padding: '4px', fontSize: '11px' }}>
                                                                        <strong>{f.id_proveedor}</strong> · {f.nom_proveedor}
                                                                    </td>
                                                                )}
                                                                <td style={{ padding: '4px' }}>{f.tipo_doc}</td>
                                                                <td style={{ padding: '4px' }}>
                                                                    <strong>#{f.nfactura}</strong>
                                                                    <span style={{ color: '#888', fontSize: '10px' }}> · {f.sucursal}-{f.boca}</span>
                                                                </td>
                                                                <td style={{ padding: '4px' }}>{fecha(f.fecha)}</td>
                                                                <td style={{ padding: '4px', fontSize: '11px' }}>{f.timbrado}</td>
                                                                <td style={{ padding: '4px', textAlign: 'right' }}>{fmt(f.mon_total)}</td>
                                                                <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600' }}>{fmt(f.saldo)}</td>
                                                                <td style={{ padding: '4px', textAlign: 'right' }}>
                                                                    {tildado ? (
                                                                        <input type="number" min="0" step="any"
                                                                            value={facSel[k]}
                                                                            max={f.saldo}
                                                                            onChange={(e) => cambiarImporteFactura(f, e.target.value)}
                                                                            style={{ width: '100px', padding: '3px 6px',
                                                                                     textAlign: 'right', border: '1px solid #ddd',
                                                                                     borderRadius: '4px' }} />
                                                                    ) : '-'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    <tr style={{ background: '#E4F2FC', fontWeight: 'bold' }}>
                                                        <td colSpan={formulario.variosProveedores ? 8 : 7}
                                                            style={{ padding: '6px', textAlign: 'right' }}>
                                                            TOTAL IMPUTADO:
                                                        </td>
                                                        <td style={{ padding: '6px', textAlign: 'right' }}>
                                                            {fmt(totalImputado)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Medios de pago */}
                            <div style={{ padding: '10px', background: '#E8F5E9', borderRadius: '4px', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                                    <h4 style={{ margin: 0, color: '#2E7D32' }}>Medios de pago</h4>
                                    <button type="button" onClick={agregarMedio} style={{
                                        marginLeft: '15px', padding: '4px 12px', background: '#2E7D32', color: '#fff',
                                        border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px'
                                    }}>+ Agregar</button>
                                    <span style={{ marginLeft: 'auto', fontWeight: '600' }}>
                                        Total: <span style={{ color: '#2E7D32' }}>{fmt(totalMedios)}</span>
                                    </span>
                                </div>
                                {medios.length === 0 ? (
                                    <p style={{ margin: '6px 0 0', color: '#888', fontSize: '12px' }}>
                                        Agregue al menos un medio (Efectivo, Cheque, Transferencia, etc.)
                                    </p>
                                ) : (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                        <thead style={{ background: '#66BB6A', color: '#fff' }}>
                                            <tr>
                                                <th style={thSt}>Cuenta</th>
                                                <th style={thSt}>Tipo Doc *</th>
                                                <th style={thSt}>Nro Doc</th>
                                                <th style={thSt}>Fecha</th>
                                                <th style={thSt}>Vencimiento</th>
                                                <th style={{...thSt, textAlign: 'right'}}>Monto *</th>
                                                <th style={thSt}>A Nombre de</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {medios.map((m, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '4px' }}>
                                                        <select value={m.idBanco}
                                                            onChange={(e) => actualizarMedio(i, 'idBanco', e.target.value)}
                                                            style={{ width: '100%', padding: '3px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                                            <option value="">-- {esCheque(m) ? 'obligatorio' : 'CAJA'} --</option>
                                                            {bancos.map(b => (
                                                                <option key={b.id_banco} value={String(b.id_banco).trim()}>
                                                                    {String(b.id_banco).trim()} - {String(b.nom_bco || '').trim()}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '4px' }}>
                                                        <select value={m.tipoDoc}
                                                            onChange={(e) => actualizarMedio(i, 'tipoDoc', e.target.value)}
                                                            style={{ width: '100%', padding: '3px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                                            {tiposDoc.map(t => (
                                                                <option key={t.id_tipodoc} value={String(t.id_tipodoc).trim()}>
                                                                    {String(t.id_tipodoc).trim()} - {String(t.nom_tipodoc || '').trim()}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '4px' }}>
                                                        <input type="number" value={m.nrocheque}
                                                            placeholder={esCheque(m) ? 'obligatorio' : 'auto'}
                                                            onChange={(e) => actualizarMedio(i, 'nrocheque', e.target.value)}
                                                            style={{ width: '100px', padding: '3px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                                    </td>
                                                    <td style={{ padding: '4px' }}>
                                                        <input type="date" value={m.fecha}
                                                            onChange={(e) => actualizarMedio(i, 'fecha', e.target.value)}
                                                            style={{ padding: '3px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                                    </td>
                                                    <td style={{ padding: '4px' }}>
                                                        <input type="date" value={m.vencimiento}
                                                            onChange={(e) => actualizarMedio(i, 'vencimiento', e.target.value)}
                                                            style={{ padding: '3px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                                    </td>
                                                    <td style={{ padding: '4px', textAlign: 'right' }}>
                                                        <input type="number" min="0" step="any" value={m.monto}
                                                            onChange={(e) => actualizarMedio(i, 'monto', e.target.value)}
                                                            style={{ width: '110px', padding: '3px 6px', textAlign: 'right',
                                                                     border: '1px solid #ddd', borderRadius: '4px' }} />
                                                    </td>
                                                    <td style={{ padding: '4px' }}>
                                                        <input type="text" value={m.detalle} maxLength={100}
                                                            placeholder={esCheque(m) ? 'Beneficiario del cheque' : ''}
                                                            onChange={(e) => actualizarMedio(i, 'detalle', e.target.value)}
                                                            style={{ width: '100%', padding: '3px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                                    </td>
                                                    <td style={{ padding: '4px' }}>
                                                        <button type="button" onClick={() => quitarMedio(i)}
                                                            style={{ background: 'none', border: '1px solid #C62828',
                                                                     color: '#C62828', borderRadius: '4px', padding: '2px 6px' }}>🗑</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Resumen y submit */}
                            <div style={{ padding: '10px', background: '#F5F5F5', borderRadius: '4px',
                                         marginBottom: '10px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <div>Imputado: <strong>{fmt(totalImputado)}</strong></div>
                                <div>Medios: <strong>{fmt(totalMedios)}</strong></div>
                                <div>
                                    Retencion:
                                    <input type="number" min="0" step="any"
                                        value={formulario.retencion}
                                        onChange={(e) => setFormulario(f => ({ ...f, retencion: e.target.value }))}
                                        style={{ width: '110px', padding: '3px 6px', textAlign: 'right',
                                                 border: '1px solid #ddd', borderRadius: '4px', marginLeft: '4px' }} />
                                </div>
                                <div style={{ marginLeft: 'auto' }}>
                                    Diferencia: {' '}
                                    <strong style={{
                                        color: Math.abs(totalImputado - totalMedios - Number(formulario.retencion || 0)) < 0.01
                                            ? '#2E7D32' : '#C62828'
                                    }}>
                                        {fmt(totalImputado - totalMedios - Number(formulario.retencion || 0))}
                                    </strong>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={cerrarModal}
                                    style={{ padding: '10px 20px', background: '#F5F5F5', border: '1px solid #ddd',
                                             borderRadius: '4px', cursor: 'pointer' }}>Cancelar</button>
                                <button type="submit"
                                    style={{ padding: '10px 24px', background: '#004F9F', color: '#fff',
                                             border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
                                    Guardar Orden de Pago
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ================= MODAL VER ================= */}
            {modalVerAbierto && ordenVer && (
                <div className="atm-modal-overlay" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'flex-start', padding: '15px', zIndex: 1000,
                    overflowY: 'auto'
                }}>
                    <div style={{ background: '#fff', borderRadius: '8px', padding: '20px',
                                 width: '92%', maxWidth: '1000px' }}>
                        <h3 style={{ color: '#004F9F', marginTop: 0 }}>
                            Orden de Pago #{ordenVer.ordenpago} {chipEstado(ordenVer.estado)}
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '13px' }}>
                            <div><strong>Fecha:</strong> {fecha(ordenVer.fecha)}</div>
                            <div><strong>Proveedor:</strong> {ordenVer.nom_proveedor} (RUC {ordenVer.ruc_prov})</div>
                            <div><strong>Moneda:</strong> {ordenVer.id_moneda} (cambio {ordenVer.tipocambio})</div>
                            <div><strong>Monto total:</strong> {fmt(ordenVer.monto)}</div>
                            <div><strong>Retencion:</strong> {fmt(ordenVer.retencion)}</div>
                            <div><strong>Cta. Bancaria:</strong> {ordenVer.nom_banco || '-'}</div>
                            <div style={{ gridColumn: '1 / span 2' }}><strong>Detalle:</strong> {ordenVer.detalle}</div>
                        </div>

                        <h4 style={{ marginTop: '15px', color: '#2E7D32' }}>Forma de Pago</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead style={{ background: '#66BB6A', color: '#fff' }}>
                                <tr>
                                    <th style={thSt}>Cuenta</th>
                                    <th style={thSt}>Tipo Doc</th>
                                    <th style={thSt}>Nro Doc</th>
                                    <th style={thSt}>Fecha</th>
                                    <th style={thSt}>Vencimiento</th>
                                    <th style={{...thSt, textAlign: 'right'}}>Monto</th>
                                    <th style={thSt}>A Nombre de</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(ordenVer.medios || []).map(m => (
                                    <tr key={`${m.id_banco}-${m.nrocheque}`} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '4px' }}>{m.nom_banco || m.id_banco}</td>
                                        <td style={{ padding: '4px' }}>{m.nom_tipodoc || m.tipo_doc}</td>
                                        <td style={{ padding: '4px' }}>{m.nrocheque}</td>
                                        <td style={{ padding: '4px' }}>{fecha(m.fecha)}</td>
                                        <td style={{ padding: '4px' }}>{fecha(m.vencimiento)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>{fmt(m.monto)}</td>
                                        <td style={{ padding: '4px', fontSize: '11px' }}>{m.detalle}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <h4 style={{ marginTop: '15px', color: '#E65100' }}>Facturas imputadas</h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead style={{ background: '#FFB300', color: '#fff' }}>
                                <tr>
                                    <th style={thSt}>Tipo</th>
                                    <th style={thSt}>N° Fact</th>
                                    <th style={thSt}>Timbrado</th>
                                    <th style={thSt}>Fecha</th>
                                    <th style={{...thSt, textAlign: 'right'}}>Total</th>
                                    <th style={{...thSt, textAlign: 'right'}}>Importe pagado</th>
                                    <th style={{...thSt, textAlign: 'right'}}>Saldo actual</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(ordenVer.facturas || []).map(f => (
                                    <tr key={`${f.tipo_doc}-${f.nfactura}`} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '4px' }}>{f.tipo_doc}</td>
                                        <td style={{ padding: '4px' }}><strong>#{f.nfactura}</strong></td>
                                        <td style={{ padding: '4px', fontSize: '11px' }}>{f.timbrado}</td>
                                        <td style={{ padding: '4px' }}>{fecha(f.fac_fecha)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>{fmt(f.fac_mon_total)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600' }}>{fmt(f.importe)}</td>
                                        <td style={{ padding: '4px', textAlign: 'right' }}>{fmt(f.fac_saldo)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => imprimir(ordenVer)}
                                style={{ padding: '8px 20px', background: '#fff', color: '#004F9F',
                                         border: '1px solid #004F9F', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>
                                🖨 Imprimir PDF
                            </button>
                            <button onClick={() => setModalVerAbierto(false)}
                                style={{ padding: '8px 20px', background: '#004F9F', color: '#fff',
                                         border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputSt = { width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' };
const lblSt   = { fontSize: '12px', color: '#555', display: 'block', marginBottom: '2px' };
const thSt    = { padding: '6px', textAlign: 'left' };

export default BOPago;
